/**
 * NBPLCCarbonValueBridge — Carbon Value Bridge for NB
 * Route: /corporate/nbplc/value-bridge
 */
import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useNBPLC } from "@/context/NBPLCContext";
import { PageHeader, SectionCard, MetricCard } from "@/components/ds";
import { Filter, X, RefreshCw, ArrowRight, ChevronRight } from "lucide-react";
import client from "@/api/client";

const STATUS_COLORS = {
  identified:             "bg-slate-100 text-slate-500 border-slate-200",
  promoted_to_strategy:   "bg-blue-100 text-blue-700 border-blue-200",
  structuring_in_progress:"bg-amber-100 text-amber-700 border-amber-200",
  financing_review:       "bg-purple-100 text-purple-700 border-purple-200",
};
const STATUS_LABELS = {
  identified:"Identified", promoted_to_strategy:"In Strategy",
  structuring_in_progress:"Structuring", financing_review:"In Financing",
};
const OPP_ICONS = {
  solar_pv:"☀️", biomass_boiler:"🌿", heat_recovery:"♻️",
  returnable_bottles:"🍺", biogas_waste:"💧", logistics_optimization:"🚚",
};

function Pill({ label, active, onClick }) {
  return (
    <button onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-all whitespace-nowrap ${
        active ? "bg-[#0F172A] text-white border-transparent" : "bg-white border-slate-200 text-slate-500 hover:border-slate-400"
      }`}>
      {label}
    </button>
  );
}

export default function NBPLCCarbonValueBridge() {
  const navigate = useNavigate();
  const { selectedPeriod, period, periods, setSelectedPeriod,
          vbCache, vbLoading, invalidateVb } = useNBPLC();
  const [selected, setSelected] = useState(null);
  const [transitioning, setTrans] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterBu, setFilterBu]     = useState("all");
  const [showFilters, setShowFilters] = useState(false);

  const vbData  = selectedPeriod ? (vbCache[selectedPeriod] || null) : null;
  const loading = vbLoading && !vbData;
  const portfolio = vbData?.portfolio || {};
  const opps      = vbData?.opportunities || [];
  const byStatus  = vbData?.by_status || {};

  const handleTransition = async (opp_id, newStatus) => {
    setTrans(true);
    await client.post(`/nbplc/opportunities/${opp_id}/transition`, { status: newStatus });
    invalidateVb(selectedPeriod);
    setTrans(false);
  };

  const buOptions = useMemo(() => {
    const seen = {};
    opps.forEach(o => { if (o.bu_id) seen[o.bu_id] = o.bu_name || o.bu_id; });
    return Object.entries(seen).map(([id,name]) => ({id, name}));
  }, [opps]);

  const filtered = useMemo(() => opps
    .filter(o => filterStatus === "all" || o.status === filterStatus)
    .filter(o => filterBu === "all" || o.bu_id === filterBu),
  [opps, filterStatus, filterBu]);

  const rankMap = useMemo(() => {
    const sorted = [...opps].sort((a,b) => (b.composite_score||0) - (a.composite_score||0));
    const m = {};
    sorted.forEach((o,i) => { m[o.opp_id] = i+1; });
    return m;
  }, [opps]);

  const viewMetrics = useMemo(() => {
    const src = (filterStatus !== "all" || filterBu !== "all") ? filtered : opps;
    const capex = src.reduce((s,o) => s+(o.capex_usd||0), 0);
    const savings = src.reduce((s,o) => s+(o.cost_savings_usd_yr||0), 0);
    const circ   = src.reduce((s,o) => s+(o.circularity_value_usd_yr||0), 0);
    const carbon = src.reduce((s,o) => s+(o.carbon_rev_base_usd_yr||0), 0);
    const red    = src.reduce((s,o) => s+(o.reduction_tco2e_yr||0), 0);
    return { capex, savings, circ, opValue: savings+circ, carbon, red,
             blended: savings > 0 ? (capex/savings).toFixed(1)+"yr" : "—", count: src.length };
  }, [filtered, opps, filterStatus, filterBu]);

  const activeFilters = [filterStatus, filterBu].filter(f => f !== "all").length;

  if (loading && !vbData) return (
    <div className="p-6 space-y-4">{[1,2].map(i=><div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
  );

  return (
    <div className={`p-6 lg:p-8 space-y-6 ${selected ? "mr-96" : ""}`}>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Nigeria Breweries Plc</p>
          <PageHeader title="Carbon Value Bridge" />
          <p className="text-xs text-slate-400 mt-0.5">{period?.label} · Where is the value?</p>
        </div>
        <select value={selectedPeriod||""} onChange={e=>setSelectedPeriod(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white">
          {periods.map(p=><option key={p.period_id} value={p.period_id}>{p.label}</option>)}
        </select>
      </div>

      {/* Portfolio validation signal */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[10px] font-semibold text-teal-700 flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-teal-500 inline-block"/>
          Portfolio validated — consistent with monthly brewery calculation output
        </p>
        <div className="flex items-center gap-3">
          {portfolio.coverage_note && (
            <p className="text-[10px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2.5 py-0.5">
              {portfolio.coverage_note}
            </p>
          )}
          <p className="text-[10px] text-slate-400">Current view: {viewMetrics.count} of {opps.length} opportunities</p>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="nb-vb-kpis">
        <MetricCard label="Total Reduction" value={`${(viewMetrics.red/1000).toFixed(0)}K`} unit="tCO₂e/yr" color="text-emerald-700" sub={`${viewMetrics.count} opportunities`}/>
        <MetricCard label="Operational Value" value={`$${(viewMetrics.opValue/1e6).toFixed(0)}M`} unit="/yr" color="text-teal-700" sub={`Cost savings + circularity`}/>
        <MetricCard label="Total CAPEX" value={`$${(viewMetrics.capex/1e6).toFixed(0)}M`} color="text-slate-900" sub={`Blended payback: ${viewMetrics.blended}`}/>
        <MetricCard label="Indicative Carbon Value" value={`$${(viewMetrics.carbon/1e6).toFixed(0)}M`} unit="/yr (cond.)" color="text-blue-700" sub="Subject to VVB validation"/>
      </div>

      {/* A/B Value Band */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="bg-slate-900 px-5 py-2.5 flex items-center justify-between">
          <p className="text-[10px] font-bold text-white uppercase tracking-widest">Portfolio Aggregated Value</p>
          <p className="text-[9px] text-slate-400">Aggregated from brewery-level opportunity data</p>
        </div>
        <div className="grid grid-cols-2">
          <div className="bg-emerald-50 border-r border-slate-200 p-5">
            <p className="text-[9px] font-bold text-emerald-700 uppercase mb-1">A. Operational Value</p>
            <p className="text-2xl font-bold text-emerald-800 mb-2">${(viewMetrics.opValue/1e6).toFixed(0)}M/yr</p>
            <div className="space-y-1 pt-2 border-t border-emerald-200 text-xs">
              <div className="flex justify-between"><span className="text-emerald-600">Energy savings</span><span className="font-semibold text-emerald-800">${(viewMetrics.savings/1e6).toFixed(0)}M/yr</span></div>
              <div className="flex justify-between"><span className="text-emerald-600">Circularity value</span><span className="font-semibold text-emerald-800">${(viewMetrics.circ/1e6).toFixed(0)}M/yr</span></div>
            </div>
          </div>
          <div className="bg-blue-50 p-5">
            <p className="text-[9px] font-bold text-blue-700 uppercase mb-1">B. Indicative Carbon Value</p>
            <p className="text-2xl font-bold text-blue-800 mb-2">${(viewMetrics.carbon/1e6).toFixed(0)}M/yr</p>
            <p className="text-[8px] text-red-500 font-semibold mt-3">Must NOT be included in base DSCR until VVB validation confirmed.</p>
          </div>
        </div>
      </div>

      {/* Opportunity Table */}
      <SectionCard>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <p className="text-xs text-slate-700 font-bold">Opportunity Table
              <span className="font-normal text-slate-400 ml-1">— {filtered.length === opps.length ? `all ${opps.length}` : `${filtered.length} of ${opps.length} (filtered)`}</span>
            </p>
            <p className="text-[9px] text-slate-400 mt-0.5">Brewery reduction levers — from monthly calculation output</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {[["all",`All (${opps.length})`],["identified",`Identified (${byStatus.identified||0})`],["promoted_to_strategy",`Strategy (${byStatus.promoted_to_strategy||0})`]].map(([s,l]) => (
              <Pill key={s} label={l} active={filterStatus===s} onClick={()=>setFilterStatus(s)}/>
            ))}
            <button onClick={() => setShowFilters(v=>!v)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold border ${showFilters||activeFilters>0?"bg-slate-700 text-white border-transparent":"bg-white border-slate-200 text-slate-500"}`}>
              <Filter size={9}/> Filter{activeFilters>0?` (${activeFilters})`:""}</button>
            {activeFilters > 0 && <button onClick={() => { setFilterStatus("all"); setFilterBu("all"); }} className="text-[10px] text-red-500 font-semibold">Clear</button>}
          </div>
        </div>
        {showFilters && (
          <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-xl">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Brewery</p>
                <div className="flex flex-wrap gap-1">
                  <Pill label="All Plants" active={filterBu==="all"} onClick={()=>setFilterBu("all")}/>
                  {buOptions.map(b=><Pill key={b.id} label={b.name.replace("NB ","")} active={filterBu===b.id} onClick={()=>setFilterBu(b.id)}/>)}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="border-b border-slate-100">
              {["#","Opportunity","Brewery","Lever Type","Reduction tCO₂e/yr","Op. Value $/yr","Carbon $/yr","CAPEX","IRR","Status",""].map(h=>(
                <th key={h} className="text-left text-[9px] text-slate-400 pb-2 pr-2 font-semibold uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {filtered.map(o => (
                <tr key={o.opp_id} className={`border-b border-slate-50 cursor-pointer hover:bg-slate-50/70 ${selected?.opp_id===o.opp_id?"bg-teal-50/50":""}`}
                  onClick={() => setSelected(o)}>
                  <td className="py-2 pr-2 text-[10px] font-bold text-slate-500">#{rankMap[o.opp_id]||"—"}</td>
                  <td className="py-2 pr-3 font-semibold text-slate-900 max-w-[160px] truncate">{o.name}</td>
                  <td className="py-2 pr-3 text-slate-500 text-[10px]">{(o.bu_name||"").replace("NB ","").split(" ")[0]}</td>
                  <td className="py-2 pr-3 text-slate-500 text-[10px]">{(o.opp_type||"").replace(/_/g," ")}</td>
                  <td className="py-2 pr-3 font-bold text-emerald-700 tabular-nums">{Number(o.reduction_tco2e_yr||0).toLocaleString()}</td>
                  <td className="py-2 pr-3 font-bold text-teal-700">${(o.guaranteed_value_usd_yr/1e6).toFixed(1)}M</td>
                  <td className="py-2 pr-3 text-blue-600">${(o.carbon_rev_base_usd_yr/1e6).toFixed(1)}M</td>
                  <td className="py-2 pr-3 text-slate-700">${(o.capex_usd/1e6).toFixed(0)}M</td>
                  <td className="py-2 pr-3 text-emerald-700 font-bold">{o.irr_pct||"—"}%</td>
                  <td className="py-2 pr-2">
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${STATUS_COLORS[o.status]||STATUS_COLORS.identified}`}>{STATUS_LABELS[o.status]||o.status}</span>
                  </td>
                  <td className="py-2">
                    {o.status === "identified" ? (
                      <button onClick={e=>{e.stopPropagation();handleTransition(o.opp_id,"promoted_to_strategy");}}
                        className="px-2 py-1 bg-[#0F172A] text-white text-[9px] font-bold rounded-lg hover:bg-slate-700">→ Strategy</button>
                    ) : <ChevronRight size={11} className="text-slate-300"/>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Detail panel */}
      {selected && (
        <>
          <div className="fixed inset-0 bg-black/10 z-40" onClick={() => setSelected(null)}/>
          <div className="fixed inset-y-0 right-0 w-80 bg-white border-l border-slate-200 shadow-2xl z-50 flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 bg-slate-50">
              <p className="text-sm font-bold text-slate-900 truncate pr-2">{selected.name}</p>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                <p className="text-xs font-bold text-emerald-700 uppercase mb-2">A. Operational Value</p>
                {[["Energy savings", `$${((selected.cost_savings_usd_yr||0)/1e6).toFixed(2)}M/yr`],
                  ["Circularity", `$${((selected.circularity_value_usd_yr||0)/1e6).toFixed(2)}M/yr`],
                  ["Total", `$${((selected.guaranteed_value_usd_yr||0)/1e6).toFixed(2)}M/yr`]].map(([l,v])=>(
                  <div key={l} className="flex justify-between text-xs py-1 border-b border-emerald-100 last:border-0">
                    <span className="text-emerald-600">{l}</span>
                    <span className="font-bold text-emerald-800">{v}</span>
                  </div>
                ))}
              </div>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
                <p className="text-xs font-bold text-slate-600 uppercase mb-2">Investment</p>
                {[["CAPEX",`$${((selected.capex_usd||0)/1e6).toFixed(1)}M`],["Payback",`${selected.payback_years||"—"}yr`],["IRR",`${selected.irr_pct||"—"}%`]].map(([l,v])=>(
                  <div key={l} className="flex justify-between text-xs py-1">
                    <span className="text-slate-500">{l}</span>
                    <span className="font-bold text-slate-900">{v}</span>
                  </div>
                ))}
              </div>
            </div>
            {selected.status === "identified" && (
              <div className="px-5 py-4 border-t border-slate-100">
                <button onClick={() => handleTransition(selected.opp_id, "promoted_to_strategy")}
                  disabled={transitioning}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#0F172A] text-white text-sm font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50">
                  {transitioning ? <RefreshCw size={14} className="animate-spin"/> : <ArrowRight size={14}/>}
                  Promote to Strategy
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
